/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useCallback, useEffect, useRef, useState } from "react"
import {
  ChatOutlined,
  ChevronLeftUniversal,
  CloseUniversal,
  FeedbackOutlined,
  HelpOutlined,
  MoreHorizontalUniversal,
  TickMarkFilled,
} from "@duxton-web/icons"
import {
  Alert,
  Button,
  Divider,
  Dropdown,
  Modal,
  notification,
  Typography,
} from "@duxton-web/ui"

import { ChatItemPickerSheet } from "ecommerce/components/ChatItemPickerSheet"
import { ChatUnavailableModal } from "ecommerce/components/ChatUnavailableModal"
import { ContactGrabUnavailableModal } from "ecommerce/components/ContactGrabUnavailableModal"
import { MoreActionsSheet } from "ecommerce/components/MoreActionsSheet"
import { ReturnInspectionPanel } from "ecommerce/components/ReturnInspectionPanel"
import { ReturnReviewPanel } from "ecommerce/components/ReturnReviewPanel"
import { ReturnStatusTimeline } from "ecommerce/components/ReturnStatusTimeline"
import {
  INSPECTION_OUTCOME,
  InspectionDecision,
  RETURN_ALERT_MESSAGE,
  RETURN_CONTEXTUAL_ACTION_LABEL,
  RETURN_HELP_CENTRE_URL,
  RETURN_MEDIA_UPLOAD_PURPOSE,
  RETURN_OVERFLOW_DEEPLINK,
  RETURN_REFUND_TEXT,
  RETURN_STATUS,
  RETURN_NATIVE_HANDLER,
  RETURN_STATUS_LABEL,
  ReturnContextualAction,
  ReturnRequestDetail,
  ReturnRequestItem,
  ReviewDecision,
} from "ecommerce/constants/returnRefund"
import { useCanActionReturnRequest } from "ecommerce/hooks/useCanActionReturnRequest"
import { notifyNativeHandler } from "ecommerce/utils/notifyNativeHandler"
import {
  formatSlaCountdownCompact,
  getClarifyState,
  hasActionableItems,
  isClosedRequest,
  isDecisionComplete,
  isInspectionDecisionComplete,
  RETURN_CLARIFY_STATE,
} from "ecommerce/utils/returnRefundHelpers"
import { isWebview } from "utils/webview"

import { ReturnRequestItemRow } from "./ReturnRequestItemRow"
import styles from "./ReturnRequestContent.modules.css"

const { Text, Title } = Typography

export const CONTENT_VIEW = {
  detail: "DETAIL",
  review: "REVIEW",
  inspection: "INSPECTION",
} as const

export type ContentView = typeof CONTENT_VIEW[keyof typeof CONTENT_VIEW]

/**
 * Driving the view from outside is all-or-nothing, so the union carries that rather than a
 * comment: `view` alone freezes the screen (the internal setter still runs, but the supplied
 * value wins), and `onViewChange` alone never sees its result take effect.
 *
 * Supplied, the view comes from the URL — what the webview page needs so the native back
 * button can step from review back to the request. Omitted, it stays in local state, which is
 * what the desktop drawer wants: a drawer view change should not become a history entry.
 */
type ViewControl =
  | { view: ContentView; onViewChange(view: ContentView): void }
  | { view?: never; onViewChange?: never }

type ReturnRequestContentProps = ViewControl & {
  detail: ReturnRequestDetail
  /**
   * What leaving the request means for this host, which decides the leading icon: the
   * drawer dismisses (a close), a page navigates (a back chevron). Host-supplied rather
   * than sniffed from `isWebview()` — the header is unconditional chrome, and the webview
   * reference reserves `isWebview()` for app-context behaviour.
   */
  leavingAction?: "close" | "back"
  /**
   * Leaving the request entirely — dismissing the drawer on desktop, returning to the
   * native screen in a webview. Stepping back out of the review is handled internally.
   */
  onClose(): void
  /** Handles `Track here` / `Contact Driver` / `Contact Grab support`. */
  onContextualAction(
    action: ReturnContextualAction,
    requestID: string,
    /** The action's server-supplied destination, when it carried one. */
    href?: string,
  ): void
  /**
   * Submits the per-item decisions as one set — mixed outcomes are allowed.
   * Resolves `true` on server acceptance; `false` keeps the review form open.
   */
  onSubmitReview(
    requestID: string,
    decisions: Record<string, ReviewDecision>,
  ): Promise<boolean>
  /**
   * Submits decision 2's per-item outcomes. Resolves true on success so this
   * content can run the success feedback (sheet or toast — the outcome decides).
   */
  onSubmitInspection(
    requestID: string,
    decisions: Record<string, InspectionDecision>,
  ): Promise<boolean>
  /**
   * Submits the items' REQUEST_INFO decisions as one batch and resolves the
   * chatroom the server opened (or reused) for them — see the hook.
   * Undefined = the submit failed (already toasted).
   */
  onRequestClarification(
    requestID: string,
    items: ReturnRequestItem[],
  ): Promise<string | undefined>
  /**
   * Stages one added photo as a server-side draft. This content binds the
   * purpose — `REVIEW` from the reject form, `INSPECTION` from the issue form.
   */
  onUploadPhoto(
    item: ReturnRequestItem,
    uploadPurpose: string,
    file: File,
    mediaSource?: string,
  ): Promise<string>
  /**
   * Deletes one server-staged draft photo. Purpose-bound here like the upload.
   */
  onRemovePhoto(
    item: ReturnRequestItem,
    uploadPurpose: string,
    assetID: string,
  ): Promise<boolean>
  /** True while any photo upload or removal is still in flight — see the hook. */
  isMediaBusy: boolean
}

/**
 * The request itself — header, scrolling body and pinned footer — with no host chrome.
 *
 * Deliberately owns its own header and footer rather than using Duxton `Drawer`'s `title` /
 * `extra` / `footer` slots: the app will eventually render this content as a full page in a
 * chromeless webview (BP-54654) where no Drawer exists, and routing the layout through
 * Drawer props would mean two different layouts to keep in step. The host supplies only the
 * surface it sits on.
 */
export const ReturnRequestContent: FC<ReturnRequestContentProps> = ({
  detail,
  view: controlledView,
  onViewChange,
  leavingAction = "close",
  onClose,
  onContextualAction,
  onSubmitReview,
  onSubmitInspection,
  onRequestClarification,
  onUploadPhoto,
  onRemovePhoto,
  isMediaBusy,
}) => {
  /*
   * Chat is owned here rather than by the host. The modal is terminal — no availability check,
   * no chat UI, one dismiss — so nothing outside needs to know it opened. Making it a prop
   * only meant every host had to wire it.
   */
  const [isChatOpen, setIsChatOpen] = useState(false)

  /*
   * The overflow actions are owned here for the same reason chat is: every destination
   * is terminal (a Help Centre link, an app deeplink, or a directions modal), so
   * routing them through host props only meant every host had to wire them —
   * `onContactGrab`/`onRaiseIssue` used to exist and all three hosts stubbed or
   * near-stubbed them.
   */
  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false)
  const [isContactGrabOpen, setIsContactGrabOpen] = useState(false)

  /*
   * The detail↔review swap is local state on desktop, where a drawer view change must not
   * become a browser history entry. The webview page hands `view`/`onViewChange` in instead
   * so each step is a real URL the native back button can unwind.
   */
  const [internalView, setInternalView] = useState<ContentView>(
    CONTENT_VIEW.detail,
  )
  const view = controlledView ?? internalView
  const isReviewing = view === CONTENT_VIEW.review
  const isInspecting = view === CONTENT_VIEW.inspection

  const setView = (next: ContentView) => {
    if (onViewChange) {
      onViewChange(next)

      return
    }

    setInternalView(next)
  }

  const isSteppingBack = isReviewing || isInspecting || leavingAction === "back"

  /*
   * Buyer chat exists only in the app, so the hand-off is app-context behaviour — one of
   * the uses the webview reference reserves `isWebview()` for. The header itself renders
   * unconditionally; only where the chat button leads differs.
   */
  const isWebviewMode = isWebview()

  /*
   * The desktop destination for `Report an issue` and `Learn about Return and
   * Refund` — the merchant Help Centre root, a placeholder until product supplies
   * the real article URLs. Desktop only: the webview reaches its Help Centre
   * through a deeplink `href`, never scripted navigation (see `overflowActions`).
   */
  const openHelpCentre = () => {
    window.open(RETURN_HELP_CENTRE_URL, "_blank", "noopener")
  }

  /*
   * One list feeds both presentations of the overflow menu — the desktop `Dropdown`
   * (labels + `onPress`) and the webview `MoreActionsSheet` (icon rows navigating by
   * `deeplink`) — so the entries cannot drift apart per host.
   *
   * The split matters: `onPress` is the desktop behaviour (Contact Grab's directions
   * modal, the Help Centre site), while in the app the sheet renders each `deeplink`
   * as a real anchor — WKWebView reports an anchor click as `.linkActivated`, which
   * is what the app's URL interception keys on; a scripted `location.href` arrives
   * as `.other` and navigates nowhere. Same rule as the timeline's action links.
   */
  const overflowActions = [
    {
      key: "contactGrab",
      icon: <ChatOutlined />,
      label: RETURN_REFUND_TEXT.overflowContactGrab,
      deeplink: RETURN_OVERFLOW_DEEPLINK.contactGrab,
      onPress: () => setIsContactGrabOpen(true),
    },
    {
      key: "reportIssue",
      icon: <FeedbackOutlined />,
      label: RETURN_REFUND_TEXT.overflowReportIssue,
      deeplink: RETURN_OVERFLOW_DEEPLINK.helpCentre,
      onPress: openHelpCentre,
    },
    {
      key: "learnMore",
      icon: <HelpOutlined />,
      label: RETURN_REFUND_TEXT.overflowLearnMore,
      deeplink: RETURN_OVERFLOW_DEEPLINK.helpCentre,
      onPress: openHelpCentre,
    },
  ]
  /*
   * Decisions live here rather than in the panel because `Confirm` sits in the footer,
   * which needs to know whether every item has been decided.
   */
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>({})

  /*
   * Clarifying is more than a chat hand-off: it is the item's once-per-item
   * REQUEST_INFO decision, so the webview first submits it (the server opens
   * the buyer chatroom in the same save, attaching every submitted item) and
   * then hands the returned room to the app — a failed submit already toasted,
   * so nothing opens. Two entries share this: the action sheet's `Initiate
   * chat` row (one item) and the header chat button's item picker (the
   * merchant's selection).
   *
   * Desktop keeps the chat-unavailable modal WITHOUT submitting: spending the
   * one formal request where no chat can follow would strand the item. The
   * WEBVIEW alone is the gate — deliberately not the bridge, so the flow stays
   * exercisable in a browser with ?webview=true; a missing bridge is reported
   * by `notifyNativeHandler` at hand-off time instead.
   */
  const [isChatPickerOpen, setIsChatPickerOpen] = useState(false)

  /*
   * Rooms opened by THIS session's clarify submits, by item id. The submit
   * invalidates the detail query, but that refetch is asynchronous — without
   * this overlay a reopened picker still shows the submitted items as
   * selectable and lets a duplicate REQUEST_INFO race the refresh. Server
   * state converges to the same answer, so the overlay never needs undoing;
   * it only resets when the drawer moves to another request.
   */
  const [clarifiedRooms, setClarifiedRooms] = useState<Record<string, string>>(
    {},
  )

  useEffect(() => {
    setClarifiedRooms({})
  }, [detail.requestID])

  const effectiveItems = detail.items.map(item =>
    clarifiedRooms[item.id] === undefined
      ? item
      : {
          ...item,
          infoRequestUsed: true,
          // "" (a response without a room) stays absent — spent, not chattable.
          roomID: clarifiedRooms[item.id] || item.roomID,
        },
  )

  const openChatRoom = (roomID?: string) =>
    notifyNativeHandler(RETURN_NATIVE_HANDLER.chat, detail.orderID, roomID)

  /*
   * The shared clarify submit: one REQUEST_INFO batch for the given items, then
   * the chat hand-off with the room the save opened. Callers are already
   * webview-gated. A failed submit has toasted and nothing opens.
   */
  const handleClarifyItems = async (
    items: ReturnRequestItem[],
  ): Promise<boolean> => {
    const roomID = await onRequestClarification(detail.requestID, items)
    if (roomID === undefined) {
      return false
    }

    // Bridge the refetch window: these items are clarified NOW, not when the
    // invalidated detail lands.
    setClarifiedRooms(current => ({
      ...current,
      ...Object.fromEntries(items.map(item => [item.id, roomID])),
    }))

    /*
     * Drop any action the merchant had picked BEFORE clarifying: REQUEST_INFO just
     * landed as these items' decision, so a stale complete pick left here would pass
     * `canConfirm` and let Confirm resolve the very items the merchant said they
     * cannot decide yet. Cleared, they fall into the untouched-clarified
     * exemption instead.
     */
    setDecisions(current => {
      const next = { ...current }
      items.forEach(item => delete next[item.id])
      return next
    })

    // "" when the response carried no room — sent as-is, the app owns that fallback.
    openChatRoom(roomID)

    return true
  }

  const handleRequestClarification = async (
    item: ReturnRequestItem,
  ): Promise<boolean> => {
    if (!isWebviewMode) {
      setIsChatOpen(true)
      return true
    }

    /*
     * Already clarified, conversation already open (`Go to chat`): the room is
     * the destination itself, so hand it straight to the app — a second submit
     * would only be refused as INFO_REQUEST_ALREADY_USED.
     */
    if (item.roomID) {
      openChatRoom(item.roomID)
      return true
    }

    return handleClarifyItems([item])
  }

  /*
   * Decision 2's set, deliberately separate from decision 1's: the two screens can
   * both be visited in one drawer lifetime (a mixed-state request), and neither may
   * inherit the other's half-made choices.
   */
  const [inspectionDecisions, setInspectionDecisions] = useState<
    Record<string, InspectionDecision>
  >({})
  const [isSuccessSheetOpen, setIsSuccessSheetOpen] = useState(false)

  /*
   * Reviewable = flagged AND still awaiting decision 1. Filtering on the reason alone
   * would put already-resolved items (e.g. an auto-refunded line on a mixed-state
   * request) back up for decision, blocking Confirm on them and letting a submission
   * reopen a settled status.
   */
  const reviewableItems = effectiveItems.filter(
    item =>
      item.reason !== undefined &&
      // The server's per-item eligibility is authoritative when supplied: an empty
      // list means "not currently decidable" even in a reviewable status.
      item.eligibleActions?.length !== 0 &&
      // A statusless flagged item inherits the request's position, so it is only
      // reviewable while the request itself still awaits acknowledgement.
      (item.status === RETURN_STATUS.acknowledgingRequest ||
        (item.status === undefined &&
          detail.status === RETURN_STATUS.acknowledgingRequest)),
  )
  const canConfirm =
    // At least one COMPLETE decision (subsumes the non-empty check): with every
    // undecided item clarified there is nothing to submit, so Confirm stays off.
    reviewableItems.some(item => isDecisionComplete(decisions[item.id])) &&
    /*
     * An UNTOUCHED clarified item does not block the others: its decision is
     * paused on the buyer's answer, and holding the merchant's approve/reject
     * hostage to it would strand the mixed request this flow exists for. One the
     * merchant has started deciding anyway must still be completed — the payload
     * sends any item with a decision, partial or not.
     */
    reviewableItems.every(
      item =>
        (item.infoRequestUsed && decisions[item.id] === undefined) ||
        isDecisionComplete(decisions[item.id]),
    ) &&
    /*
     * Photos stage and delete as they are added and removed, so a submit while
     * either is in flight can race the draft the decision is about to consume.
     */
    !isMediaBusy

  /*
   * The footer's actionability is status-driven (`hasActionableItems`), which can be
   * true while nothing passes the reviewable filter (e.g. an actionable status on an
   * unflagged line). Opening an empty review screen would be a dead end — Confirm
   * could never enable — so the CTA only navigates when there is something to review.
   */
  const canReview = reviewableItems.length > 0

  /*
   * What the chat picker offers: the items the review flow could clarify — the
   * same reviewable set the panel renders, so an unflagged line, an
   * inspection-stage item or a closed request can never be checked — plus any
   * item already attached to the buyer conversation, whatever its stage, so
   * `Go to chat` keeps working after the request moves on.
   */
  const chatPickerItems = effectiveItems.filter(
    item =>
      reviewableItems.includes(item) ||
      getClarifyState(item) === RETURN_CLARIFY_STATE.chat,
  )

  /*
   * Inspectable = physically returned and awaiting the merchant's verdict. Same
   * inherit rule as the reviewable filter: a statusless item takes the request's
   * position.
   */
  const inspectableItems = detail.items.filter(
    item =>
      item.status === RETURN_STATUS.verifyingReturnedItem ||
      (item.status === undefined &&
        detail.status === RETURN_STATUS.verifyingReturnedItem),
  )
  const canConfirmInspection =
    inspectableItems.length > 0 &&
    inspectableItems.every(item =>
      isInspectionDecisionComplete(inspectionDecisions[item.id]),
    ) &&
    !isMediaBusy
  // Guards the same dead-end as `canReview`: an actionable status with nothing
  // actually inspectable must not open an empty screen.
  const canInspect = inspectableItems.length > 0

  // The detail, review and inspection screens share one scroll container; entering
  // or leaving any of them must not inherit another screen's scroll offset.
  const bodyRef = useRef<HTMLDivElement>(null)

  /*
   * The live view across the awaited submit: in the webview a native back gesture
   * can leave the review WHILE the POST is pending, and the success continuation
   * must not then request review → detail again — the page host maps that request
   * to `history.goBack()`, which would pop a second time and eject the merchant
   * from the request entirely.
   */
  const viewRef = useRef(view)

  useEffect(() => {
    viewRef.current = view
  }, [view])

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 })
  }, [view])

  /*
   * The cards send an UPDATER, not a finished decision: `onRemove` and the
   * `onChange` that Duxton fires straight after it both write within one tick,
   * so a card building its next decision from its (still stale) `decision` prop
   * would silently drop whichever write landed first — the removal tombstone.
   *
   * An updater may return undefined to LEAVE the key deleted: photo bookkeeping
   * settles asynchronously, and one landing after a clarify cleared the item's
   * decision must not resurrect it (see the card's photo updaters).
   */
  const handleDecisionChange = useCallback(
    (
      itemID: string,
      update: (previous?: ReviewDecision) => ReviewDecision | undefined,
    ) =>
      setDecisions(current => {
        const updated = update(current[itemID])

        if (updated === undefined) {
          const next = { ...current }
          delete next[itemID]
          return next
        }

        return { ...current, [itemID]: updated }
      }),
    [],
  )

  const handleInspectionChange = useCallback(
    (
      itemID: string,
      update: (previous?: InspectionDecision) => InspectionDecision,
    ) =>
      setInspectionDecisions(current => ({
        ...current,
        [itemID]: update(current[itemID]),
      })),
    [],
  )

  /*
   * Back steps out of the review view first, so a stray click on the leading icon cannot
   * discard decisions the merchant is part-way through making — they survive until the
   * drawer itself closes.
   */
  const handleBack = () => {
    if (isReviewing || isInspecting) {
      setView(CONTENT_VIEW.detail)
      return
    }

    onClose()
  }

  /*
   * Three footer states, and neither one alone decides it:
   * - any item still needs a decision  → countdown + `Review`
   * - the request is closed            → `Raise an issue`
   * - live but nothing to decide       → no footer (the in-transit frame shows none)
   *
   * On top of that the role gates the decision footers — see `renderFooter`.
   */
  const isActionable = hasActionableItems(detail)
  const isClosed = isClosedRequest(detail)
  const canActionRequest = useCanActionReturnRequest()

  // A const rather than `detail.alert?.action` inline: TS keeps const narrowing
  // inside the alert link's onClick closure, where the JSX guard's is lost.
  const alertAction = detail.alert?.action

  /*
   * Inspection is the merchant's second decision. It reuses this footer but changes the
   * label and CTA (`Verify by` / `Received`). Checked at both levels because the header
   * shows the least-progressed item — but acknowledgement (decision 1) wins whenever any
   * item still awaits it, matching the least-progressed rule the header follows: the CTA
   * must describe the earliest decision the merchant still owes.
   */
  const hasAcknowledgementPending =
    detail.status === RETURN_STATUS.acknowledgingRequest ||
    detail.items.some(
      item => item.status === RETURN_STATUS.acknowledgingRequest,
    )
  const hasInspectionPending =
    detail.status === RETURN_STATUS.verifyingReturnedItem ||
    detail.items.some(
      item => item.status === RETURN_STATUS.verifyingReturnedItem,
    )
  const isInspection = hasInspectionPending && !hasAcknowledgementPending

  /*
   * Inspection submits through a promise the host resolves; the success feedback is
   * outcome-shaped — every item approved earns the illustrated sheet, a reported
   * issue gets the neutral toast (its dedicated copy is still with product).
   */
  const handleConfirmInspection = () => {
    const isAllApproved = inspectableItems.every(
      item =>
        inspectionDecisions[item.id]?.outcome ===
        INSPECTION_OUTCOME.approveRefund,
    )

    onSubmitInspection(detail.requestID, inspectionDecisions).then(isDone => {
      if (!isDone) {
        // The failure toast has already shown; the merchant's choices survive
        // for another attempt.
        return
      }

      setInspectionDecisions({})

      // Only leave inspection if the merchant is still on it — see `viewRef`.
      if (viewRef.current === CONTENT_VIEW.inspection) {
        setView(CONTENT_VIEW.detail)
      }

      if (isAllApproved) {
        setIsSuccessSheetOpen(true)
        return
      }

      notification.success({
        message: RETURN_REFUND_TEXT.inspectionSubmitted,
      })
    })
  }

  const renderFooter = () => {
    /*
     * A cashier opens and reads a request but never decides it (BP-57853), so every
     * decision footer below is gated on the role — the `Review` / `Received` entry CTA
     * and both `Confirm`s, because the webview drives the view from the URL and a
     * cashier can reach `/review` or `/inspect` by direct link. A closed request's
     * `Raise an issue` is a support hand-off rather than a decision, so it is not gated.
     */
    // Inspecting pins `Confirm`, exactly like the review screen below.
    if (isInspecting && canActionRequest) {
      return (
        <Button
          type="primary"
          size="xlarge"
          shape="round"
          block
          disabled={!canConfirmInspection}
          onClick={handleConfirmInspection}
        >
          {RETURN_REFUND_TEXT.reviewConfirmCta}
        </Button>
      )
    }

    // Reviewing pins `Confirm` so it stays reachable however long the item list is.
    if (isReviewing && canActionRequest) {
      return (
        <Button
          type="primary"
          size="xlarge"
          shape="round"
          block
          disabled={!canConfirm}
          onClick={async () => {
            // Only a server-accepted submission leaves the review screen: on
            // failure the form (actions, reasons, photos) stays intact so the
            // merchant never has to reconstruct an irreversible decision.
            if (!(await onSubmitReview(detail.requestID, decisions))) {
              return
            }

            // Clear the set so a later review pass starts fresh instead of
            // pre-completed with last round's choices.
            setDecisions({})

            // Only leave review if the merchant is still on it — see `viewRef`.
            if (viewRef.current === CONTENT_VIEW.review) {
              setView(CONTENT_VIEW.detail)
            }
          }}
        >
          {RETURN_REFUND_TEXT.reviewConfirmCta}
        </Button>
      )
    }

    if (isActionable && canActionRequest) {
      return (
        <div className={styles.footerRow}>
          {/*
            No SLA value → no countdown: rendering `00:00` for a merely missing
            deadline would read as already expired next to an enabled CTA.
          */}
          {detail.slaMinutesLeft !== undefined ? (
            <div>
              <Text strong>
                {isInspection
                  ? RETURN_REFUND_TEXT.drawerVerifyBy
                  : RETURN_REFUND_TEXT.drawerReviewBy}
              </Text>
              <div className={styles.countdownValue}>
                {formatSlaCountdownCompact(detail.slaMinutesLeft)}
                <span>{RETURN_REFUND_TEXT.drawerMinutesSuffix}</span>
              </div>
            </div>
          ) : (
            <div />
          )}
          <Button
            type="primary"
            size="xlarge"
            shape="round"
            className={styles.reviewButton}
            /*
             * The dead-end case where actionability is status-driven but nothing
             * passes the relevant filter (e.g. a pending item the payload sent
             * without a reason) reads as disabled rather than as a button that
             * swallows the click.
             */
            disabled={isInspection ? !canInspect : !canReview}
            onClick={() => {
              if (isInspection && canInspect) {
                setView(CONTENT_VIEW.inspection)
                return
              }

              if (!isInspection && canReview) {
                setView(CONTENT_VIEW.review)
              }
            }}
          >
            {isInspection
              ? RETURN_REFUND_TEXT.drawerReceivedCta
              : RETURN_REFUND_TEXT.drawerReviewCta}
          </Button>
        </div>
      )
    }

    if (isClosed) {
      return (
        <Button
          size="xlarge"
          shape="round"
          block
          className={styles.raiseIssueButton}
          /*
           * In the app this must be a real anchor — Duxton `Button` renders one when
           * given `href` — because the deeplink only reaches the app's URL
           * interception as `.linkActivated`; see `overflowActions`. No `target`: a
           * scheme destination must stay in this document.
           */
          {...(isWebviewMode
            ? { href: RETURN_OVERFLOW_DEEPLINK.helpCentre }
            : { onClick: openHelpCentre })}
        >
          {RETURN_REFUND_TEXT.drawerRaiseIssueCta}
        </Button>
      )
    }

    return null
  }

  const footer = renderFooter()

  return (
    <div className={styles.content}>
      <header className={styles.header}>
        <Button
          type="text"
          size="small"
          shape="circle"
          aria-label={
            /*
             * A back chevron whenever leaving is a step rather than a dismissal: out of
             * the review on either host, or out of a request the host reached by
             * navigating to it.
             */
            isSteppingBack
              ? RETURN_REFUND_TEXT.drawerBackLabel
              : RETURN_REFUND_TEXT.drawerCloseLabel
          }
          icon={isSteppingBack ? <ChevronLeftUniversal /> : <CloseUniversal />}
          className={styles.headerIconButton}
          onClick={handleBack}
        />
        <Text strong ellipsis className={styles.headerTitle}>
          {/* The short order number is the display identity; `orderID` (the real
              order ID, which the native hand-offs key on) is only its fallback. */}
          {isReviewing && RETURN_REFUND_TEXT.reviewTitle}
          {isInspecting && RETURN_REFUND_TEXT.inspectionTitle}
          {!isReviewing &&
            !isInspecting &&
            (detail.orderNumber ?? detail.orderID)}
        </Text>
        <Button
          type="text"
          size="small"
          shape="circle"
          aria-label={RETURN_REFUND_TEXT.drawerChatLabel}
          icon={<ChatOutlined />}
          className={styles.headerIconButton}
          /*
           * Buyer chat only exists in the app, so a webview opens the item
           * picker — the conversation is about specific items, and clarifying
           * them IS the REQUEST_INFO decision; desktop has no chat at all and
           * shows the terminal modal that explains where to find it.
           */
          onClick={() =>
            isWebviewMode ? setIsChatPickerOpen(true) : setIsChatOpen(true)
          }
        />
        {isWebviewMode ? (
          /*
           * The app design replaces the dropdown with a bottom sheet, so in a webview
           * the trigger opens the sheet rendered below instead of anchoring a menu.
           */
          <Button
            type="text"
            size="small"
            shape="circle"
            aria-label={RETURN_REFUND_TEXT.drawerMoreActionsLabel}
            aria-haspopup="dialog"
            icon={<MoreHorizontalUniversal />}
            className={styles.headerIconButton}
            onClick={() => setIsMoreSheetOpen(true)}
          />
        ) : (
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: overflowActions.map(action => ({
                key: action.key,
                label: action.label,
                onClick: action.onPress,
              })),
            }}
          >
            <Button
              type="text"
              size="small"
              shape="circle"
              aria-label={RETURN_REFUND_TEXT.drawerMoreActionsLabel}
              // Dropdown doesn't mark its trigger, so announce the menu by hand.
              aria-haspopup="menu"
              icon={<MoreHorizontalUniversal />}
              className={styles.headerIconButton}
            />
          </Dropdown>
        )}
      </header>

      <div ref={bodyRef} className={styles.body}>
        {isInspecting && (
          <ReturnInspectionPanel
            items={inspectableItems}
            decisions={inspectionDecisions}
            onChange={handleInspectionChange}
            onUploadPhoto={(item, file, mediaSource) =>
              onUploadPhoto(
                item,
                RETURN_MEDIA_UPLOAD_PURPOSE.inspection,
                file,
                mediaSource,
              )
            }
            onRemovePhoto={(item, assetID) =>
              onRemovePhoto(
                item,
                RETURN_MEDIA_UPLOAD_PURPOSE.inspection,
                assetID,
              )
            }
          />
        )}
        {isReviewing && (
          <ReturnReviewPanel
            items={reviewableItems}
            decisions={decisions}
            onChange={handleDecisionChange}
            onUploadPhoto={(item, file, mediaSource) =>
              onUploadPhoto(
                item,
                RETURN_MEDIA_UPLOAD_PURPOSE.review,
                file,
                mediaSource,
              )
            }
            onRemovePhoto={(item, assetID) =>
              onRemovePhoto(item, RETURN_MEDIA_UPLOAD_PURPOSE.review, assetID)
            }
            onRequestChat={handleRequestClarification}
          />
        )}
        {!isInspecting && !isReviewing && (
          <>
            <div>
              <Title level={3} className={styles.status}>
                {/* The server's own status copy wins; the FE label map covers
                    mock-backed details and a response that arrives without it. */}
                {detail.statusLabel ??
                  RETURN_STATUS_LABEL[detail.status] ??
                  detail.status}
              </Title>
              <Text type="secondary">{detail.requestedAt}</Text>
            </div>

            {detail.alert && (
              <Alert
                className={styles.alert}
                type="error"
                showIcon
                message={RETURN_ALERT_MESSAGE[detail.alert.kind]}
                description={
                  // A const so the narrowing survives into the onClick closure. A real
                  // button so the hand-off is focusable and keyboard-operable — a Link
                  // without href never enters the tab order.
                  alertAction && (
                    <Button
                      type="link"
                      className={styles.alertAction}
                      onClick={() =>
                        onContextualAction(alertAction, detail.requestID)
                      }
                    >
                      {RETURN_CONTEXTUAL_ACTION_LABEL[alertAction]}
                    </Button>
                  )
                }
              />
            )}

            <Divider className={styles.divider} />

            <Text strong className={styles.sectionHeading}>
              {detail.itemsHeading ??
                RETURN_REFUND_TEXT.drawerItemsForBuyer(
                  detail.items.length,
                  detail.buyerName,
                )}
            </Text>
            {detail.items.map(item => (
              <ReturnRequestItemRow key={item.id} item={item} />
            ))}

            <Divider className={styles.divider} />

            {/*
              The whole row goes, divider included, when the server has no total yet
              (`totalValueDisplay` is a pending presentation field) — a "Total" label
              over a blank amount would read as a broken price.
            */}
            {detail.total && (
              <>
                <div className={styles.totalRow}>
                  <Text strong className={styles.totalLabel}>
                    {RETURN_REFUND_TEXT.drawerTotal}
                  </Text>
                  <Text strong className={styles.totalValue}>
                    {detail.total}
                  </Text>
                </div>

                <Divider className={styles.divider} />
              </>
            )}

            <Text strong className={styles.sectionHeading}>
              {detail.timelineTitle ??
                RETURN_REFUND_TEXT.drawerReturnStatusHeading}
            </Text>
            <ReturnStatusTimeline
              entries={detail.timeline}
              onAction={(action, href) =>
                onContextualAction(action, detail.requestID, href)
              }
            />
          </>
        )}
      </div>

      {footer && <footer className={styles.footer}>{footer}</footer>}

      {/*
        S11a's illustrated approval sheet. `Done` is the only exit by design — the
        sheet is a confirmation, not a decision, so mask/Esc dismissal is off to
        keep the exit singular.
      */}
      <Modal
        open={isSuccessSheetOpen}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        width={360}
      >
        <div className={styles.successSheet}>
          <TickMarkFilled className={styles.successIcon} />
          <Text strong className={styles.successTitle}>
            {RETURN_REFUND_TEXT.inspectionSuccessTitle}
          </Text>
          <Text type="secondary">
            {RETURN_REFUND_TEXT.inspectionSuccessBody}
          </Text>
          <Button
            type="primary"
            size="xlarge"
            shape="round"
            block
            onClick={() => setIsSuccessSheetOpen(false)}
          >
            {RETURN_REFUND_TEXT.inspectionSuccessDone}
          </Button>
        </div>
      </Modal>

      <ChatUnavailableModal
        open={isChatOpen}
        orderID={detail.orderID}
        onClose={() => setIsChatOpen(false)}
      />

      <ContactGrabUnavailableModal
        open={isContactGrabOpen}
        onClose={() => setIsContactGrabOpen(false)}
      />

      {/*
       * Webview presentation of the overflow menu — every row is a real anchor to
       * its deeplink; see `overflowActions` for why scripted navigation cannot
       * work here.
       */}
      <MoreActionsSheet
        open={isMoreSheetOpen}
        title={RETURN_REFUND_TEXT.drawerMoreActionsLabel}
        onCancel={() => setIsMoreSheetOpen(false)}
        actions={overflowActions.map(({ key, icon, label, deeplink }) => ({
          key,
          icon,
          label,
          href: deeplink,
        }))}
      />

      {/*
       * Closes before acting, like the action sheet's clarify row: the submit
       * shows the global loader, and the chat destination must not render
       * under an open sheet.
       */}
      <ChatItemPickerSheet
        open={isChatPickerOpen}
        items={chatPickerItems}
        onCancel={() => setIsChatPickerOpen(false)}
        onChat={items => {
          setIsChatPickerOpen(false)
          handleClarifyItems(items)
        }}
        onGoToChat={item => {
          setIsChatPickerOpen(false)
          openChatRoom(item.roomID)
        }}
      />
    </div>
  )
}
